import React, { useState, useEffect } from 'react';
import { X, ChevronRight, Globe, Calendar as CalendarIcon, Bell, Trash2, Plus, RefreshCw, ExternalLink } from 'lucide-react';
import { fetchCalendarPreferences, updateCalendarPreferences } from '../services/calendarPreferencesApi';
import { 
    getGoogleCalendars, 
    authorizeGoogleCalendar, 
    removeGoogleCalendar, 
    syncGoogleCalendar,
    updateGoogleCalendar 
} from '../services/googleCalendarApi';

const CalendarSettings = ({ isOpen, onClose, selectedFamily }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // My Calendars state
    const [myCalendars, setMyCalendars] = useState([
        { id: '1', name: selectedFamily?.name || 'Family Calendar', color: '#3b82f6', enabled: true }
    ]);

    // General Settings state
    const [defaultView, setDefaultView] = useState('agenda');
    const [defaultEventDuration, setDefaultEventDuration] = useState(30);
    const [publicHolidaysCountry, setPublicHolidaysCountry] = useState('');
    const [weekNumbersEnabled, setWeekNumbersEnabled] = useState(false);
    const [firstDayOfWeek, setFirstDayOfWeek] = useState('sunday');
    const [defaultReminder, setDefaultReminder] = useState('30 minutes before');
    const [defaultReminderCustom, setDefaultReminderCustom] = useState({ value: 30, unit: 'minutes' });
    const [dailyBriefEnabled, setDailyBriefEnabled] = useState(false);
    const [dailyBriefTiming, setDailyBriefTiming] = useState('same-day-morning');
    const [dailyBriefTime, setDailyBriefTime] = useState('08:00');
    const [calendarCleanupMonths, setCalendarCleanupMonths] = useState(6);

    // Google Calendar state
    const [googleCalendars, setGoogleCalendars] = useState([]);
    const [showAddGoogleCalendarModal, setShowAddGoogleCalendarModal] = useState(false);
    const [addingGoogleCalendar, setAddingGoogleCalendar] = useState(false);
    const [syncingCalendarId, setSyncingCalendarId] = useState(null);
    const [googleCalendarForm, setGoogleCalendarForm] = useState({
        calendar_email: '',
        calendar_name: '',
        initials: '',
        color: '#3b82f6'
    });

    // Load preferences when modal opens
    useEffect(() => {
        if (isOpen) {
            loadPreferences();
            loadGoogleCalendars();
        }
    }, [isOpen, selectedFamily]);

    const loadPreferences = async () => {
        try {
            setLoading(true);
            const prefs = await fetchCalendarPreferences();
            
            // Map backend data to frontend state
            setDefaultView(prefs.default_view || 'agenda');
            setDefaultEventDuration(prefs.default_event_duration || 30);
            setPublicHolidaysCountry(prefs.public_holidays_country || '');
            setWeekNumbersEnabled(prefs.week_numbers_enabled || false);
            setFirstDayOfWeek(prefs.first_day_of_week || 'sunday');
            
            // Map reminder type
            if (prefs.default_reminder_type) {
                const reminderMap = {
                    'at_time': 'At time of Event',
                    '5_min': '5 minutes before',
                    '15_min': '15 minutes before',
                    '30_min': '30 minutes before',
                    '1_hour': '1 hour before',
                    'custom': 'Custom'
                };
                setDefaultReminder(reminderMap[prefs.default_reminder_type] || '30 minutes before');
                if (prefs.default_reminder_type === 'custom') {
                    setDefaultReminderCustom({
                        value: prefs.default_reminder_custom_value || 30,
                        unit: prefs.default_reminder_custom_unit || 'minutes'
                    });
                }
            } else {
                setDefaultReminder('30 minutes before');
            }
            
            setDailyBriefEnabled(prefs.daily_brief_enabled || false);
            setDailyBriefTiming(prefs.daily_brief_timing || 'same-day-morning');
            setDailyBriefTime(prefs.daily_brief_time || '08:00');
            setCalendarCleanupMonths(prefs.calendar_cleanup_months || 6);
        } catch (error) {
            console.error('Error loading calendar preferences:', error);
            // Use defaults if loading fails
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            
            // Map frontend reminder format to backend format
            const reminderMap = {
                'At time of Event': 'at_time',
                '5 minutes before': '5_min',
                '15 minutes before': '15_min',
                '30 minutes before': '30_min',
                '1 hour before': '1_hour',
                'Custom': 'custom'
            };
            
            const preferencesData = {
                default_view: defaultView,
                default_event_duration: defaultEventDuration,
                public_holidays_country: publicHolidaysCountry || null,
                week_numbers_enabled: weekNumbersEnabled,
                first_day_of_week: firstDayOfWeek,
                default_reminder_type: reminderMap[defaultReminder] || null,
                default_reminder_custom_value: defaultReminder === 'Custom' ? defaultReminderCustom.value : null,
                default_reminder_custom_unit: defaultReminder === 'Custom' ? defaultReminderCustom.unit : null,
                daily_brief_enabled: dailyBriefEnabled,
                daily_brief_timing: dailyBriefEnabled ? dailyBriefTiming : null,
                daily_brief_time: dailyBriefEnabled ? dailyBriefTime : null,
                calendar_cleanup_months: calendarCleanupMonths
            };
            
            await updateCalendarPreferences(preferencesData);
            onClose();
        } catch (error) {
            console.error('Error saving calendar preferences:', error);
            alert(`Failed to save settings: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const countries = [
        'United States', 'United Kingdom', 'Canada', 'Australia', 'India', 
        'Germany', 'France', 'Japan', 'China', 'Brazil', 'Mexico', 'Spain',
        'Italy', 'Netherlands', 'Sweden', 'Norway', 'Denmark', 'Finland',
        'Singapore', 'South Korea', 'New Zealand', 'South Africa', 'Argentina'
    ];

    const reminderOptions = [
        'At time of Event',
        '5 minutes before',
        '15 minutes before',
        '30 minutes before',
        '1 hour before',
        'Custom'
    ];

    const cleanupOptions = [1, 3, 6, 12, 24, 36];

    const loadGoogleCalendars = async () => {
        if (!selectedFamily) return;
        try {
            const response = await getGoogleCalendars(selectedFamily.id);
            setGoogleCalendars(response.items || []);
        } catch (error) {
            console.error('Error loading Google Calendars:', error);
        }
    };

    const handleAddGoogleCalendar = async () => {
        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        if (!googleCalendarForm.calendar_email || !googleCalendarForm.calendar_name || !googleCalendarForm.initials) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            setAddingGoogleCalendar(true);
            const response = await authorizeGoogleCalendar({
                calendar_email: googleCalendarForm.calendar_email,
                family_id: selectedFamily.id,
                calendar_name: googleCalendarForm.calendar_name,
                initials: googleCalendarForm.initials,
                color: googleCalendarForm.color
            });

            // Redirect to Google OAuth
            if (response.authorization_url) {
                window.location.href = response.authorization_url;
            }
        } catch (error) {
            console.error('Error initiating Google Calendar OAuth:', error);
            alert(`Failed to connect Google Calendar: ${error.message}`);
            setAddingGoogleCalendar(false);
        }
    };

    const handleRemoveGoogleCalendar = async (integrationId) => {
        if (!window.confirm('Are you sure you want to remove this Google Calendar? Events will be deleted.')) {
            return;
        }

        try {
            await removeGoogleCalendar(integrationId);
            await loadGoogleCalendars();
        } catch (error) {
            console.error('Error removing Google Calendar:', error);
            alert(`Failed to remove Google Calendar: ${error.message}`);
        }
    };

    const handleSyncGoogleCalendar = async (integrationId) => {
        try {
            setSyncingCalendarId(integrationId);
            const response = await syncGoogleCalendar(integrationId);
            alert(`Successfully synced ${response.events_synced} events`);
            await loadGoogleCalendars();
        } catch (error) {
            console.error('Error syncing Google Calendar:', error);
            alert(`Failed to sync Google Calendar: ${error.message}`);
        } finally {
            setSyncingCalendarId(null);
        }
    };

    const handleToggleGoogleCalendarSync = async (integration) => {
        try {
            await updateGoogleCalendar(integration.id, {
                sync_enabled: !integration.sync_enabled
            });
            await loadGoogleCalendars();
        } catch (error) {
            console.error('Error updating Google Calendar:', error);
            alert(`Failed to update Google Calendar: ${error.message}`);
        }
    };

    const colorOptions = [
        '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
        '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
    ];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-white">Calendar Settings</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6 text-white" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <>
                    {/* My Calendars */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-gray-900">My Calendars</h3>
                        </div>
                        <div className="space-y-3">
                            {myCalendars.map((calendar) => (
                                <div key={calendar.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="flex items-center gap-4">
                                        <div 
                                            className="w-4 h-4 rounded-full" 
                                            style={{ backgroundColor: calendar.color }}
                                        ></div>
                                        <div>
                                            <p className="font-medium text-gray-900">{calendar.name}</p>
                                            <p className="text-sm text-gray-500">mifamli Calendar</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={calendar.enabled}
                                                onChange={(e) => {
                                                    setMyCalendars(myCalendars.map(c => 
                                                        c.id === calendar.id ? { ...c, enabled: e.target.checked } : c
                                                    ));
                                                }}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Other Calendars */}
                    <div className="mb-8">
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Google Calendars</h3>
                        <div className="space-y-3">
                            {googleCalendars.map((calendar) => (
                                <div key={calendar.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="flex items-center gap-4 flex-1">
                                        <div 
                                            className="w-4 h-4 rounded-full flex-shrink-0" 
                                            style={{ backgroundColor: calendar.color }}
                                        ></div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-gray-900">{calendar.calendar_name}</p>
                                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                                                    {calendar.initials}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500">{calendar.calendar_email}</p>
                                            {calendar.last_synced_at && (
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Last synced: {new Date(calendar.last_synced_at).toLocaleString()}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleSyncGoogleCalendar(calendar.id)}
                                            disabled={syncingCalendarId === calendar.id}
                                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                            title="Sync now"
                                        >
                                            <RefreshCw className={`w-4 h-4 ${syncingCalendarId === calendar.id ? 'animate-spin' : ''}`} />
                                        </button>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={calendar.sync_enabled}
                                                onChange={() => handleToggleGoogleCalendarSync(calendar)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                        <button
                                            onClick={() => handleRemoveGoogleCalendar(calendar.id)}
                                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Remove calendar"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                onClick={() => setShowAddGoogleCalendarModal(true)}
                                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                                        <Plus className="w-5 h-5 text-gray-600" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-medium text-gray-900">Add a Google Calendar</p>
                                        <p className="text-sm text-gray-500">Sync events from your Google Calendar</p>
                                    </div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>
                    </div>

                    {/* General Settings */}
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">General Settings</h3>
                        <div className="space-y-6">
                            {/* Public Holidays */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Public Holidays
                                </label>
                                <p className="text-sm text-gray-500 mb-3">
                                    Show public holidays from selected country on your calendar
                                </p>
                                <select
                                    value={publicHolidaysCountry}
                                    onChange={(e) => setPublicHolidaysCountry(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">Select a country</option>
                                    {countries.map(country => (
                                        <option key={country} value={country}>{country}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Week Numbers */}
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Week Numbers
                                    </label>
                                    <p className="text-sm text-gray-500">
                                        Show week numbers in calendar view and list view
                                    </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={weekNumbersEnabled}
                                        onChange={(e) => setWeekNumbersEnabled(e.target.checked)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                            </div>

                            {/* 1st Day of Week */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    1st Day of Week
                                </label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="firstDayOfWeek"
                                            value="sunday"
                                            checked={firstDayOfWeek === 'sunday'}
                                            onChange={(e) => setFirstDayOfWeek(e.target.value)}
                                            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-gray-700">Sunday</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="firstDayOfWeek"
                                            value="monday"
                                            checked={firstDayOfWeek === 'monday'}
                                            onChange={(e) => setFirstDayOfWeek(e.target.value)}
                                            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-gray-700">Monday</span>
                                    </label>
                                </div>
                            </div>

                            {/* Default View */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Default View
                                </label>
                                <p className="text-sm text-gray-500 mb-3">
                                    Choose the default calendar view when opening the calendar
                                </p>
                                <select
                                    value={defaultView}
                                    onChange={(e) => setDefaultView(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="day">Day</option>
                                    <option value="3-day">3-Day</option>
                                    <option value="week">Week</option>
                                    <option value="month">Month</option>
                                    <option value="agenda">Agenda</option>
                                </select>
                            </div>

                            {/* Default Event Duration */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Default Event Duration
                                </label>
                                <p className="text-sm text-gray-500 mb-3">
                                    Set the default duration for new events
                                </p>
                                <select
                                    value={defaultEventDuration}
                                    onChange={(e) => setDefaultEventDuration(parseInt(e.target.value))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value={15}>15 minutes</option>
                                    <option value={30}>30 minutes</option>
                                    <option value={60}>1 hour</option>
                                    <option value={120}>2 hours</option>
                                </select>
                            </div>

                            {/* Default Reminder */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Default Reminder
                                </label>
                                <select
                                    value={defaultReminder}
                                    onChange={(e) => setDefaultReminder(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
                                >
                                    {reminderOptions.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                                {defaultReminder === 'Custom' && (
                                    <div className="flex items-center gap-3 mt-3">
                                        <input
                                            type="number"
                                            min="1"
                                            value={defaultReminderCustom.value}
                                            onChange={(e) => setDefaultReminderCustom({ ...defaultReminderCustom, value: parseInt(e.target.value) || 1 })}
                                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                        <select
                                            value={defaultReminderCustom.unit}
                                            onChange={(e) => setDefaultReminderCustom({ ...defaultReminderCustom, unit: e.target.value })}
                                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="minutes">Minutes</option>
                                            <option value="hours">Hours</option>
                                            <option value="days">Days</option>
                                        </select>
                                        <span className="text-gray-600">before</span>
                                    </div>
                                )}
                            </div>

                            {/* Daily Brief Notification */}
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Daily Brief Notification
                                        </label>
                                        <p className="text-sm text-gray-500">
                                            Receive a daily notification with a preview of your schedule
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={dailyBriefEnabled}
                                            onChange={(e) => setDailyBriefEnabled(e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>
                                {dailyBriefEnabled && (
                                    <div className="space-y-4 mt-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Notification Timing
                                            </label>
                                            <div className="space-y-2">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="dailyBriefTiming"
                                                        value="same-day-morning"
                                                        checked={dailyBriefTiming === 'same-day-morning'}
                                                        onChange={(e) => setDailyBriefTiming(e.target.value)}
                                                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="text-gray-700">The same day, in the morning</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="dailyBriefTiming"
                                                        value="day-before-evening"
                                                        checked={dailyBriefTiming === 'day-before-evening'}
                                                        onChange={(e) => setDailyBriefTiming(e.target.value)}
                                                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="text-gray-700">The day before, in the evening</span>
                                                </label>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Time
                                            </label>
                                            <input
                                                type="time"
                                                value={dailyBriefTime}
                                                onChange={(e) => setDailyBriefTime(e.target.value)}
                                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Calendar Cleanup */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Calendar Cleanup
                                </label>
                                <p className="text-sm text-gray-500 mb-3">
                                    Automatically clear past calendar events after the selected time period
                                </p>
                                <select
                                    value={calendarCleanupMonths}
                                    onChange={(e) => setCalendarCleanupMonths(parseInt(e.target.value))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {cleanupOptions.map(months => (
                                        <option key={months} value={months}>
                                            {months} {months === 1 ? 'month' : 'months'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Add Google Calendar Modal */}
            {showAddGoogleCalendarModal && (
                <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-gray-900">Add Google Calendar</h3>
                            <button
                                onClick={() => {
                                    setShowAddGoogleCalendarModal(false);
                                    setGoogleCalendarForm({ calendar_email: '', calendar_name: '', initials: '', color: '#3b82f6' });
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Calendar Email *
                                </label>
                                <input
                                    type="email"
                                    value={googleCalendarForm.calendar_email}
                                    onChange={(e) => setGoogleCalendarForm({ ...googleCalendarForm, calendar_email: e.target.value })}
                                    placeholder="user@example.com"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Display Name *
                                </label>
                                <input
                                    type="text"
                                    value={googleCalendarForm.calendar_name}
                                    onChange={(e) => setGoogleCalendarForm({ ...googleCalendarForm, calendar_name: e.target.value })}
                                    placeholder="John's Calendar"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Initials (2-3 characters) *
                                </label>
                                <input
                                    type="text"
                                    value={googleCalendarForm.initials}
                                    onChange={(e) => setGoogleCalendarForm({ ...googleCalendarForm, initials: e.target.value.toUpperCase().slice(0, 3) })}
                                    placeholder="JD"
                                    maxLength={3}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Color
                                </label>
                                <div className="flex gap-2 flex-wrap">
                                    {colorOptions.map((color) => (
                                        <button
                                            key={color}
                                            onClick={() => setGoogleCalendarForm({ ...googleCalendarForm, color })}
                                            className={`w-10 h-10 rounded-lg border-2 transition-all ${
                                                googleCalendarForm.color === color ? 'border-gray-900 scale-110' : 'border-gray-300'
                                            }`}
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAddGoogleCalendarModal(false);
                                    setGoogleCalendarForm({ calendar_email: '', calendar_name: '', initials: '', color: '#3b82f6' });
                                }}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddGoogleCalendar}
                                disabled={addingGoogleCalendar || !googleCalendarForm.calendar_email || !googleCalendarForm.calendar_name || !googleCalendarForm.initials}
                                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {addingGoogleCalendar ? 'Connecting...' : 'Connect Calendar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarSettings;

